import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  ApiKey,
  ApiKeyFormData,
  ScopeInfo,
  createApiKey,
  groupScopes,
  updateApiKey,
} from "../../services/apiKeyService";

interface ApiKeyFormModalProps {
  show: boolean;
  onHide: () => void;
  scopes: ScopeInfo[];
  editingKey?: ApiKey | null;
  onSaved: (created?: { raw_key: string; id: number }) => void;
}

const emptyForm: ApiKeyFormData = {
  name: "",
  description: "",
  contact_email: "",
  scopes: [],
  expires_at: "",
};

const ApiKeyFormModal: React.FC<ApiKeyFormModalProps> = ({
  show,
  onHide,
  scopes,
  editingKey,
  onSaved,
}) => {
  const [form, setForm] = useState<ApiKeyFormData>(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const scopeGroups = useMemo(() => groupScopes(scopes), [scopes]);

  useEffect(() => {
    if (!show) return;
    if (editingKey) {
      setForm({
        name: editingKey.name,
        description: editingKey.description || "",
        contact_email: editingKey.contact_email || "",
        scopes: editingKey.scopes,
        expires_at: editingKey.expires_at ? editingKey.expires_at.slice(0, 16) : "",
        is_active: editingKey.is_active,
      });
    } else {
      setForm(emptyForm);
    }
    setError("");
  }, [show, editingKey]);

  const toggleScope = (scopeId: string) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scopeId)
        ? prev.scopes.filter((s) => s !== scopeId)
        : [...prev.scopes, scopeId],
    }));
  };

  const selectGroup = (groupScopesList: ScopeInfo[], checked: boolean) => {
    const ids = groupScopesList.map((s) => s.id);
    setForm((prev) => {
      const without = prev.scopes.filter((s) => !ids.includes(s));
      return checked ? { ...prev, scopes: [...without, ...ids] } : { ...prev, scopes: without };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (form.scopes.length === 0) {
      setError("Select at least one permission");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editingKey) {
        await updateApiKey(editingKey.id, form);
        onSaved();
      } else {
        const created = await createApiKey(form);
        onSaved({ raw_key: created.raw_key, id: created.id });
      }
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save API key");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="api-keys-page">
      <Modal.Header closeButton className="border-secondary">
        <Modal.Title>{editingKey ? "Edit API Key" : "Create API Key"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" className="ak-alert ak-alert-danger">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Acme Corp integration"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Contact email</Form.Label>
            <Form.Control
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="Who this key is issued to"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional notes about where this key is used"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Expires</Form.Label>
            <Form.Control
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            />
            <Form.Text className="text-muted">Leave blank for no expiration.</Form.Text>
          </Form.Group>

          {editingKey && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="api-key-active"
                label="Active"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
            </Form.Group>
          )}

          <Form.Label className="mb-2">Permissions</Form.Label>
          <p className="text-muted small mb-3">
            Select read and write scopes per resource. Keys only access routes allowed by their permissions.
          </p>

          {Object.entries(scopeGroups).map(([group, groupScopesList]) => {
            const allSelected = groupScopesList.every((s) => form.scopes.includes(s.id));
            const someSelected = groupScopesList.some((s) => form.scopes.includes(s.id));
            return (
              <div key={group} className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold small">{group}</span>
                  <Button
                    type="button"
                    size="sm"
                    className="btn-ak-outline"
                    onClick={() => selectGroup(groupScopesList, !allSelected)}
                  >
                    {allSelected ? "Clear group" : someSelected ? "Select all in group" : "Select all in group"}
                  </Button>
                </div>
                <div className="d-flex flex-column gap-2">
                  {groupScopesList.map((scope) => (
                    <div key={scope.id} className="scope-row p-2 d-flex align-items-start gap-2">
                      <Form.Check
                        type="checkbox"
                        id={`scope-${scope.id}`}
                        checked={form.scopes.includes(scope.id)}
                        onChange={() => toggleScope(scope.id)}
                        className="mt-1"
                      />
                      <div>
                        <label htmlFor={`scope-${scope.id}`} className="fw-semibold mb-0 small">
                          {scope.label}
                        </label>
                        <div className="text-muted small">{scope.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-ak-outline" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button className="btn-ak-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : editingKey ? "Save Changes" : "Create Key"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ApiKeyFormModal;
